import { api } from './api';
import { User } from '../types';

export interface NutritionStats {
  period: string;
  totalEntries: number;
  totalFoods: number;
  uniqueFoods: string[];
  foodCounts: Record<string, number>;
  nutritionTotals: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    vitamins: Record<string, number>;
    minerals: Record<string, number>;
  };
  entries: any[];
}

export interface NutritionAnalysis {
  analysis: string;
  structuredAnalysis: {
    progress: {
      consumed: number;
      total: number;
      percentage: number;
    };
    missingNutrients: string[];
    suggestions: string[];
    analysis: string;
  };
  stats: NutritionStats;
}

// Obtener estadísticas nutricionales
export const getNutritionStats = async (userId: string, days: number = 7): Promise<NutritionStats> => {
  try {
    const response = await api.get(`/nutrition-stats?userId=${userId}&days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo estadísticas nutricionales:', error);
    throw error;
  }
};

// Realizar análisis nutricional con IA
export const analyzeNutrition = async (userId: string, userProfile?: User): Promise<NutritionAnalysis> => {
  try {
    const response = await api.post('/nutrition-analysis', {
      userId,
      userProfile
    });
    console.log('Respuesta cruda de /nutrition-analysis:', response);
    // Devuelve el análisis, sea response.data o response directamente
    if (response && typeof response === 'object') {
      if ('data' in response && response.data) {
        return response.data;
      }
      return response;
    }
    throw new Error('Respuesta inesperada del backend');
  } catch (error) {
    console.error('Error en análisis nutricional:', error);
    throw error;
  }
};

// Obtener análisis nutricional cacheado
export const getCachedNutritionAnalysis = async (userId: string) => {
  try {
    const response = await api.get(`/nutrition-analysis-cache?userId=${userId}`, {
      headers: {
        'Cache-Control': 'no-cache'
      }
    });
    
    // Log para depuración
    console.log('Respuesta bruta de la API en getCachedNutritionAnalysis:', response);
    
    // Extraer los datos de la respuesta
    const data = response.data || response;
    console.log('Datos extraídos:', data);
    
    // Si los datos ya tienen la estructura correcta, devolverlos directamente
    if (data && typeof data === 'object' && 'structuredAnalysis' in data && 'stats' in data) {
      console.log('✅ Estructura correcta encontrada directamente');
      return data;
    }
    
    // Si no, buscar en la propiedad 'analysis' si existe
    if (data && typeof data === 'object' && 'analysis' in data && typeof data.analysis === 'object') {
      console.log('🔍 Buscando en data.analysis:', data.analysis);
      
      // Verificar si data.analysis tiene la estructura correcta
      if ('structuredAnalysis' in data.analysis && 'stats' in data.analysis) {
        console.log('✅ Estructura correcta encontrada en data.analysis');
        return data.analysis;
      }
      
      // Si data.analysis no tiene la estructura correcta, buscar más profundo
      if ('analysis' in data.analysis && typeof data.analysis.analysis === 'object') {
        console.log('🔍 Buscando en data.analysis.analysis:', data.analysis.analysis);
        
        if ('structuredAnalysis' in data.analysis.analysis && 'stats' in data.analysis.analysis) {
          console.log('✅ Estructura correcta encontrada en data.analysis.analysis');
          return data.analysis.analysis;
        }
      }
    }
    
    console.warn('❌ No se encontró la estructura correcta en ningún nivel:', data);
    return null;
  } catch (error: any) {
    if (error.message?.includes('No hay análisis cacheado') || error.message?.includes('404')) {
      console.log('ℹ️ No hay análisis nutricional cacheado disponible aún');
      throw new Error('NO_CACHED_ANALYSIS');
    }
    console.error('Error obteniendo análisis cacheado:', error);
    throw error;
  }
};
